import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Button, Typography, useTheme, useMediaQuery, LinearProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useLocation, useNavigate } from "react-router-dom";
import GameScreenShell from "@components/Home/Games/GameScreenShell";
import SessionSummary, { SessionSummaryStats } from "@components/Home/Games/SessionSummary";
import type { GameSessionGame } from "@models/game";
import { GAME_IDS } from "@models/game";
import {
  useStartSessionMutation,
  useSubmitRoundMutation,
  useCompleteSessionMutation,
  useGetContentQuery,
} from "@store/gameApi";
import { RootState } from "@store/store";
import type { WordFluencyTapItem } from "./wordFluencyTapContent";
import {
  getLevelParams,
  resolveRoundContent,
  computeWordFluencyRoundMetrics,
  type LevelParams,
  type RoundContent,
} from "./wordFluencyTapContent";
import {
  getContentBankLabel,
  getContentBankVoiceDescription,
} from "@utils/contentBankTranslations";
import {
  gameContinueButtonSx,
  gamePrimaryButtonSx,
} from "@components/Home/Games/gameUi";
import { useGameTimingMode } from "@hooks/useGameTimingMode";
import { useInGameMoreTimeAllowed } from "@hooks/useInGameMoreTimeAllowed";
import { adjustWordFluencyTapTiming, getInGameMoreTimeBonusMs } from "@utils/gameTiming";
import {
  buildImageCandidateUrls,
  preloadImageUrls,
} from "@utils/gameImage";
import { alpha } from "@mui/material/styles";
import GameAvatarOverlay, {
  GameAvatarOverlayHandle,
} from "@components/GameAvatarOverlay";
import { useVoiceOverPref } from "@hooks/useVoiceOverPref";

export interface WordFluencyTapGuidedProps {
  guidedSessionId?: string;
  guidedCurrentGame?: GameSessionGame;
  guidedGameIndex?: number;
  guidedTotalGames?: number;
  onGuidedGameComplete?: () => void;
}

const ROUNDS_PER_SESSION = 3;
const SESSION_START_DEDUPE_MS = 2000;
const GRID_GAP = 8;

let lastSessionStartAt = 0;

type Phase = "idle" | "playing" | "feedback";

interface FreePlayLocationState {
  freePlaySessionId?: string;
  freePlayGames?: GameSessionGame[];
}

const WordFluencyTap: FC<WordFluencyTapGuidedProps> = ({
  guidedSessionId,
  guidedCurrentGame,
  guidedGameIndex = 0,
  guidedTotalGames = 1,
  onGuidedGameComplete,
}) => {
  const location = useLocation();
  const freePlayState = location.state as FreePlayLocationState | null;
  const freePlaySessionId = freePlayState?.freePlaySessionId;
  const freePlayGame = freePlayState?.freePlayGames?.[0];

  const isGuided = Boolean(guidedSessionId && guidedCurrentGame);
  const isFreePlayFromLibrary = Boolean(freePlaySessionId && freePlayGame);
  const effectiveSessionId = guidedSessionId ?? freePlaySessionId ?? null;
  const effectiveGame = guidedCurrentGame ?? freePlayGame;

  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const gridCols = isMobile ? 3 : 4;
  const tileSizePx = useMemo(() => (isMobile ? 92 : 100), [isMobile]);

  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId
  );
  const sessionStartedRef = useRef(false);
  const [startSession] = useStartSessionMutation();
  const [sessionStartError, setSessionStartError] = useState<string | null>(null);
  const [submitRound] = useSubmitRoundMutation();
  const [completeSession] = useCompleteSessionMutation();

  const contentSeed = effectiveGame?.contentSeed ?? "";
  const difficulty = effectiveGame
    ? Math.max(1, Math.min(10, effectiveGame.difficulty))
    : 1;
  const gameTimingMode = useGameTimingMode();
  const inGameMoreTimeAllowed = useInGameMoreTimeAllowed();
  const levelParams = useMemo<LevelParams>(
    () => adjustWordFluencyTapTiming(getLevelParams(difficulty), gameTimingMode),
    [difficulty, gameTimingMode]
  );

  const contentSeedForFetch =
    contentSeed ||
    (effectiveSessionId
      ? `${effectiveSessionId}-${GAME_IDS.wordFluencyTap}`
      : "");
  const { data: contentData, error: contentError } = useGetContentQuery(
    { gameId: GAME_IDS.wordFluencyTap, seed: contentSeedForFetch },
    { skip: !contentSeedForFetch }
  );

  const [sessionId, setSessionId] = useState<string | null>(effectiveSessionId);
  const [phase, setPhase] = useState<Phase>("idle");
  const [roundCount, setRoundCount] = useState(0);
  const [roundContent, setRoundContent] = useState<RoundContent | null>(null);
  const [selectedTileIds, setSelectedTileIds] = useState<Set<string>>(new Set());
  const [selectionOrder, setSelectionOrder] = useState<string[]>([]);
  const [roundStartTime, setRoundStartTime] = useState(0);
  const [roundTimeRemaining, setRoundTimeRemaining] = useState(0);
  const [xpEarned, setXpEarned] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [sessionScore, setSessionScore] = useState(0);
  const [roundResults, setRoundResults] = useState<{ accuracy: number; completionMs: number }[]>([]);
  const [domainUpdatesSum, setDomainUpdatesSum] = useState<Record<string, number>>({});
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [summaryStats, setSummaryStats] = useState<SessionSummaryStats | null>(null);
  const [muted, setMuted] = useState(false);
  const [voiceOverEnabled, setVoiceOverEnabled] = useVoiceOverPref(
    GAME_IDS.wordFluencyTap
  );
  const [avatarReady, setAvatarReady] = useState(false);
  const [roundBonusSeconds, setRoundBonusSeconds] = useState(0);
  const [moreTimeUsedForRound, setMoreTimeUsedForRound] = useState(false);
  const [imageAttemptById, setImageAttemptById] = useState<Record<string, number>>(
    {}
  );
  const submittedForRoundRef = useRef(false);
  const sessionSeedRef = useRef<string | null>(null);
  const avatarRef = useRef<GameAvatarOverlayHandle | null>(null);
  const tileVoiceDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sessionSeed = useMemo(() => {
    if (sessionSeedRef.current) return sessionSeedRef.current;
    sessionSeedRef.current = `session-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    return sessionSeedRef.current;
  }, []);

  const targetCategorySet = useMemo(() => {
    if (!roundContent) return new Set<string>();
    return new Set(
      roundContent.tiles
        .filter((t) => t.categories.includes(roundContent.targetCategory))
        .map((t) => t.id)
    );
  }, [roundContent]);

  const startNewRound = useCallback(
    (roundIndex?: number) => {
      submittedForRoundRef.current = false;
      const idx = roundIndex ?? roundCount;
      const content = resolveRoundContent(
        contentSeed,
        idx,
        levelParams,
        contentData,
        contentError,
        sessionSeed
      );
      setRoundContent(content);
      setImageAttemptById({});
      setSelectedTileIds(new Set());
      setSelectionOrder([]);
      const now = Date.now();
      setRoundStartTime(now);
      setRoundTimeRemaining(levelParams.timeSeconds);
      setRoundBonusSeconds(0);
      setMoreTimeUsedForRound(false);
      setXpEarned(null);
      setPhase("playing");
    },
    [contentSeed, roundCount, levelParams, contentData, contentError, sessionSeed]
  );

  useEffect(() => {
    if (!contentData) return;
    const previewRound = resolveRoundContent(
      contentSeed,
      roundCount,
      levelParams,
      contentData,
      contentError,
      sessionSeed
    );
    const urls = previewRound.tiles.flatMap((tile) =>
      buildImageCandidateUrls(tile.imageUrl)
    );
    const controller = new AbortController();
    preloadImageUrls(urls, { concurrency: 4, signal: controller.signal }).catch(
      () => {}
    );
    return () => controller.abort();
  }, [
    contentData,
    contentSeed,
    roundCount,
    levelParams,
    contentError,
    sessionSeed,
  ]);

  useEffect(() => {
    if (isGuided || isFreePlayFromLibrary) return;
    if (!selectedPatientId) {
      setSessionStartError(t("games.errors.onlyPatientCanPlay"));
      return;
    }
    if (sessionStartedRef.current) return;
    const now = Date.now();
    if (now - lastSessionStartAt < SESSION_START_DEDUPE_MS) return;
    lastSessionStartAt = now;
    sessionStartedRef.current = true;
    setSessionStartError(null);
    let mounted = true;
    startSession({ patientId: selectedPatientId })
      .unwrap()
      .then((res) => {
        if (mounted && res?.sessionId) {
          setSessionId(res.sessionId);
          setSessionStartTime(Date.now());
        }
      })
      .catch((err: { status?: number }) => {
        if (mounted && err?.status === 403) {
          setSessionStartError(t("games.errors.onlyPatientCanPlay"));
        }
      });
    return () => {
      mounted = false;
    };
  }, [isGuided, isFreePlayFromLibrary, selectedPatientId, startSession, t]);

  useEffect(() => {
    if ((isGuided && guidedSessionId) || isFreePlayFromLibrary) {
      setSessionStartTime(Date.now());
    }
  }, [isGuided, guidedSessionId, isFreePlayFromLibrary]);

  useEffect(() => {
    if (sessionStartTime == null || paused) return;
    const id = setInterval(() => {
      setTimerSeconds(Math.floor((Date.now() - sessionStartTime) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [sessionStartTime, paused]);

  // Countdown for current round (playing phase)
  useEffect(() => {
    if (phase !== "playing" || paused || !roundContent) return;
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - roundStartTime) / 1000);
      const remaining = Math.max(
        0,
        levelParams.timeSeconds + roundBonusSeconds - elapsed
      );
      setRoundTimeRemaining(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 200);
    return () => clearInterval(interval);
  }, [
    phase,
    paused,
    roundContent,
    roundStartTime,
    levelParams.timeSeconds,
    roundBonusSeconds,
  ]);

  const handleTileTap = useCallback(
    (tile: WordFluencyTapItem) => {
      if (phase !== "playing") return;
      if (voiceOverEnabled && !muted) {
        if (tileVoiceDebounceRef.current) {
          clearTimeout(tileVoiceDebounceRef.current);
        }
        const voice = getContentBankVoiceDescription(
          t,
          tile.id,
          tile.label,
          tile.voiceDescription
        );
        tileVoiceDebounceRef.current = setTimeout(() => {
          if (voice) {
            avatarRef.current?.speak(voice, {
              interrupt: false,
              replaceQueue: false,
            });
          }
          tileVoiceDebounceRef.current = null;
        }, 250);
      }
      setSelectedTileIds((prev) => {
        const next = new Set(prev);
        if (next.has(tile.id)) {
          next.delete(tile.id);
          setSelectionOrder((order) => order.filter((id) => id !== tile.id));
        } else {
          next.add(tile.id);
          setSelectionOrder((order) => [...order, tile.id]);
        }
        return next;
      });
    },
    [phase, voiceOverEnabled, muted, t]
  );

  const submitCurrentSelection = useCallback(() => {
    if (!roundContent || submittedForRoundRef.current) return;
    submittedForRoundRef.current = true;
    const { accuracy, correctTaps, incorrectTaps, misses, errorCount } =
      computeWordFluencyRoundMetrics(
        roundContent.tiles,
        roundContent.targetCategory,
        selectedTileIds,
      );
    const completionTimeMs = Date.now() - roundStartTime;

    setRoundResults((prev) => [...prev, { accuracy, completionMs: completionTimeMs }]);

    if (sessionId) {
      submitRound({
        sessionId,
        body: {
          gameId: GAME_IDS.wordFluencyTap,
          roundNumber: roundCount + 1,
          difficulty,
          accuracy,
          reactionTimeMs: null,
          completionTimeMs,
          errorCount,
          rawData: {
            correctTaps,
            incorrectTaps,
            misses,
            completionTimeMs,
            tileSelectionOrder: selectionOrder,
          },
        },
      })
        .unwrap()
        .then((res) => {
          if (res?.xpEarned != null) {
            setXpEarned(res.xpEarned);
            setSessionScore((s) => s + res.xpEarned);
          }
          if (res?.domainUpdates && Object.keys(res.domainUpdates).length > 0) {
            setDomainUpdatesSum((prev) => {
              const next = { ...prev };
              for (const [k, v] of Object.entries(res.domainUpdates)) {
                next[k] = (next[k] ?? 0) + v;
              }
              return next;
            });
          }
        })
        .catch(() => {});
    } else {
      const xp = accuracy >= 0.8 ? 20 : 10;
      setXpEarned(xp);
      setSessionScore((s) => s + xp);
    }
    setPhase("feedback");
  }, [
    roundContent,
    selectedTileIds,
    roundStartTime,
    selectionOrder,
    sessionId,
    roundCount,
    difficulty,
    submitRound,
  ]);

  const handleDone = useCallback(() => {
    if (phase !== "playing" || !roundContent) return;
    submitCurrentSelection();
  }, [phase, roundContent, submitCurrentSelection]);

  // Auto-submit when countdown reaches 0
  useEffect(() => {
    if (phase !== "playing" || roundTimeRemaining > 0 || !roundContent) return;
    submitCurrentSelection();
  }, [phase, roundTimeRemaining, roundContent, submitCurrentSelection]);

  const handleNextRound = useCallback(() => {
    const nextRound = roundCount + 1;
    if (nextRound >= ROUNDS_PER_SESSION) {
      if (isGuided && onGuidedGameComplete) {
        onGuidedGameComplete();
        return;
      }
      const allResults = [...roundResults];
      const totalRounds = allResults.length;
      const avgAccuracy =
        totalRounds > 0
          ? (allResults.reduce((s, r) => s + r.accuracy, 0) / totalRounds) * 100
          : 0;
      const avgCompletionSec =
        totalRounds > 0
          ? allResults.reduce((s, r) => s + r.completionMs, 0) / totalRounds / 1000
          : 0;

      if (sessionId) {
        completeSession({ sessionId })
          .unwrap()
          .then((res) => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgCompletionSec,
              xpEarned: res?.totalXp ?? sessionScore,
              domainChanges: domainUpdatesSum,
            });
          })
          .catch(() => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgCompletionSec,
              xpEarned: sessionScore,
              domainChanges: domainUpdatesSum,
            });
          });
      } else {
        setSummaryStats({
          accuracyPercent: avgAccuracy,
          avgReactionSeconds: avgCompletionSec,
          xpEarned: sessionScore,
          domainChanges: domainUpdatesSum,
        });
      }
      lastSessionStartAt = 0;
      setSessionId(null);
      setShowSessionSummary(true);
      return;
    }
    setRoundCount(nextRound);
    startNewRound(nextRound);
  }, [
    roundCount,
    roundResults,
    sessionId,
    sessionScore,
    domainUpdatesSum,
    isGuided,
    onGuidedGameComplete,
    completeSession,
    startNewRound,
  ]);

  const handleExit = useCallback(() => {
    if (sessionId) completeSession({ sessionId }).catch(() => {});
    lastSessionStartAt = 0;
    navigate(-1);
  }, [sessionId, completeSession, navigate]);

  const handleSummaryContinue = useCallback(() => navigate(-1), [navigate]);

  const handleRequestMoreTime = useCallback(() => {
    if (moreTimeUsedForRound || phase !== "playing") return;
    const bonusMs = getInGameMoreTimeBonusMs(GAME_IDS.wordFluencyTap);
    if (bonusMs <= 0) return;
    setRoundBonusSeconds((s) => s + Math.round(bonusMs / 1000));
    setMoreTimeUsedForRound(true);
  }, [moreTimeUsedForRound, phase]);

  const progress =
    phase === "idle"
      ? 0
      : (roundCount + (phase === "feedback" ? 1 : 0)) / ROUNDS_PER_SESSION;

  const feedbackText =
    phase === "feedback"
      ? t("games.wordFluencyTap.feedbackGood")
      : null;
  const canStartRound =
    !voiceOverEnabled || (!sessionId ? false : avatarReady);

  useEffect(() => {
    if (!voiceOverEnabled || muted) return;
    if (phase !== "playing" || !roundContent) return;
    avatarRef.current?.speak(
      t("games.wordFluencyTap.voicePromptCategory", {
        category: t(roundContent.targetCategoryLabelKey),
      }),
      { interrupt: true, replaceQueue: true }
    );
  }, [voiceOverEnabled, muted, phase, roundContent?.targetCategory, t]);

  useEffect(
    () => () => {
      if (tileVoiceDebounceRef.current) {
        clearTimeout(tileVoiceDebounceRef.current);
      }
      avatarRef.current?.stop();
    },
    []
  );

  if (showSessionSummary && summaryStats) {
    return (
      <SessionSummary stats={summaryStats} onContinue={handleSummaryContinue} />
    );
  }

  return (
    <GameScreenShell
      gameName={t("games.wordFluencyTap.title")}
      level={difficulty}
      progress={progress}
      feedback={feedbackText}
      timerSeconds={sessionStartTime != null ? timerSeconds : null}
      score={sessionScore > 0 ? sessionScore : null}
      paused={paused}
      onBack={handleExit}
      onPause={() => setPaused((p) => !p)}
      gameIndex={isGuided ? guidedGameIndex : undefined}
      totalGames={isGuided ? guidedTotalGames : undefined}
      muted={muted}
      onMuteChange={setMuted}
      voiceOverEnabled={voiceOverEnabled}
      onVoiceOverChange={setVoiceOverEnabled}
      moreTime={
        inGameMoreTimeAllowed && phase === "playing"
          ? {
              onRequest: handleRequestMoreTime,
              disabled: moreTimeUsedForRound,
            }
          : undefined
      }
    >
      {sessionId && (
        <GameAvatarOverlay
          ref={avatarRef}
          gameSessionId={sessionId}
          enabled={voiceOverEnabled}
          muted={muted}
          onReadyChange={setAvatarReady}
        />
      )}
      {phase === "idle" && (
        <Box
          textAlign="center"
          py={4}
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          {sessionStartError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {sessionStartError}
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            {t("games.wordFluencyTap.instructionsTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t("games.wordFluencyTap.instructions")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => startNewRound()}
            disabled={!canStartRound}
            sx={gameContinueButtonSx}
          >
            {t("games.wordFluencyTap.startRound")}
          </Button>
          {voiceOverEnabled && !canStartRound ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {t("companion.loadingMessage")}
            </Typography>
          ) : null}
        </Box>
      )}

      {(phase === "playing" || phase === "feedback") && roundContent && (
        <>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: "1.5rem", mb: 1.5, textAlign: "center" }}
          >
            <Trans
              i18nKey="games.wordFluencyTap.categoryPrompt"
              values={{ category: t(roundContent.targetCategoryLabelKey) }}
              components={{
                highlight: (
                  <Box
                    component="span"
                    sx={{
                      color: "primary.main",
                      fontWeight: 800,
                      textUnderlineOffset: 3,
                    }}
                  />
                ),
              }}
            />
          </Typography>

          {phase === "playing" && (
            <Box sx={{ mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={(roundTimeRemaining / levelParams.timeSeconds) * 100}
                color="primary"
                sx={{ height: 8, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                {t("games.wordFluencyTap.timeRemaining", {
                  seconds: roundTimeRemaining,
                })}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${gridCols}, ${tileSizePx}px)`,
              gap: `${GRID_GAP}px`,
              justifyContent: "center",
              maxWidth: "100%",
              mx: "auto",
              mb: 2,
            }}
          >
            {roundContent.tiles.map((tile) => {
              const isTarget = targetCategorySet.has(tile.id);
              const selected = selectedTileIds.has(tile.id);
              const showCorrect =
                phase === "feedback" && isTarget;
              const showIncorrect =
                phase === "feedback" && selected && !isTarget;
              const imageCandidates = buildImageCandidateUrls(tile.imageUrl);
              const imageAttempt = imageAttemptById[tile.id] ?? 0;
              const imageSrc =
                imageCandidates[Math.min(imageAttempt, imageCandidates.length - 1)];
              return (
                <Box
                  key={tile.id}
                  onClick={() => handleTileTap(tile)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleTileTap(tile);
                    }
                  }}
                  aria-pressed={selected}
                  sx={{
                    width: tileSizePx,
                    minWidth: tileSizePx,
                    height: tileSizePx + (levelParams.showLabels ? 24 : 0),
                    minHeight: tileSizePx,
                    borderRadius: 2,
                    border: "3px solid",
                    borderColor:
                      phase === "feedback"
                        ? showCorrect
                          ? "success.main"
                          : showIncorrect
                            ? "warning.main"
                            : selected
                              ? "primary.main"
                              : "divider"
                        : selected
                          ? "primary.main"
                          : "divider",
                    bgcolor:
                      phase === "feedback"
                        ? showCorrect
                          ? alpha(theme.palette.success.main, 0.2)
                          : showIncorrect
                            ? alpha(theme.palette.warning.main, 0.2)
                            : selected
                              ? alpha(theme.palette.primary.main, 0.2)
                              : "background.paper"
                        : "background.paper",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: phase === "playing" ? "pointer" : "default",
                    transition: "border-color 0.2s, background-color 0.2s",
                    position: "relative",
                    "&:hover": phase === "playing" ? { bgcolor: "action.hover" } : {},
                  }}
                >
                  <Box
                    sx={{
                      width: Math.round(tileSizePx * 0.88),
                      height: Math.round(tileSizePx * 0.88),
                      borderRadius: 1,
                      bgcolor: "action.hover",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    {imageSrc ? (
                      <Box
                        component="img"
                        src={imageSrc}
                        alt=""
                        onError={() =>
                          setImageAttemptById((prev) => ({
                            ...prev,
                            [tile.id]: Math.min(
                              (prev[tile.id] ?? 0) + 1,
                              Math.max(0, imageCandidates.length - 1)
                            ),
                          }))
                        }
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <Typography variant="h6" color="text.secondary">
                        {getContentBankLabel(t, tile.id, tile.label).slice(0, 1).toUpperCase()}
                      </Typography>
                    )}
                  </Box>
                  {levelParams.showLabels && (
                    <Typography
                      variant="caption"
                      sx={{ mt: 0.5, fontSize: "0.75rem", textAlign: "center", lineHeight: "1.1"  }}
                      color="text.secondary"
                    >
                      {getContentBankLabel(t, tile.id, tile.label)}
                    </Typography>
                  )}
                  {selected && phase !== "feedback" && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        color: "primary.main",
                      }}
                    >
                      <CheckCircleOutlineIcon fontSize="small" />
                    </Box>
                  )}
                  {phase === "feedback" && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        color: showCorrect ? "success.main" : showIncorrect ? "text.secondary" : "transparent",
                      }}
                    >
                      {showCorrect ? (
                        <CheckCircleOutlineIcon fontSize="small" />
                      ) : showIncorrect ? (
                        <RemoveCircleOutlineIcon fontSize="small" />
                      ) : null}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {phase === "playing" && (
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleDone}
              sx={{ ...gamePrimaryButtonSx, mt: 2, mb: 2 }}
            >
              {t("games.wordFluencyTap.done")}
            </Button>
          )}

          {phase === "feedback" && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
              mt={2}
              mb={2}
            >
              {xpEarned != null && (
                <Typography variant="body2" color="primary.main">
                  +{xpEarned} XP
                </Typography>
              )}
              <Button
                variant="contained"
                size="large"
                onClick={handleNextRound}
                sx={gameContinueButtonSx}
              >
                {roundCount + 1 >= ROUNDS_PER_SESSION
                  ? t("games.wordFluencyTap.finishSession")
                  : t("games.wordFluencyTap.nextRound")}
              </Button>
            </Box>
          )}
        </>
      )}
    </GameScreenShell>
  );
};

export default WordFluencyTap;
